// The developer page for businesses that integrate with the Partner API
// (FR-56, Phase 8): the four steps to a first call, copyable examples, the
// sandbox codes and the interactive reference generated from the published
// OpenAPI document. Public: no sign-in is needed to read it.
import {
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PARTNER_SPEC_URL } from "./spec";

const SwaggerView = lazy(() => import("./SwaggerView"));

const STEPS = ["register", "credentials", "networks", "purposes", "token", "approval"] as const;

const SANDBOX: [code: string, consent: string, oneTime: string][] = [
  ["10001AB", "email, phone, id_number", "approved"],
  ["10002AC", "email, phone", "approved"],
  ["10003AD", "phone", "approved"],
  ["10004AE", "none", "approved_once"],
  ["10005AF", "none", "declined"],
  ["10006AG", "none", "never"],
];

export function DevelopersPage() {
  const { t } = useTranslation();
  const base = `${window.location.origin}/api/v1`;
  const token = [
    `curl -s -X POST ${base}/partner/token \\`,
    `  -u "$CLIENT_ID:$CLIENT_SECRET" \\`,
    `  -d grant_type=client_credentials`,
  ].join("\n");
  const resolve = [
    `curl -s -X POST ${base}/partner/resolve \\`,
    `  -H "Authorization: Bearer $TOKEN" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"code": "4K57Q21", "purpose": "kyc"}'`,
  ].join("\n");

  return (
    <Stack maw={1100} mx="auto" py="xl" gap="xl">
      <Stack gap="xs">
        <Badge variant="light" w="fit-content">
          {t("developers.badge")}
        </Badge>
        <Title order={1} size="h2">
          {t("developers.title")}
        </Title>
        <Text c="dimmed" maw={720}>
          {t("developers.intro")}
        </Text>
        <Group gap="sm" mt="xs">
          <Button component="a" href="#reference">
            {t("developers.open_reference")}
          </Button>
          <Button component="a" href={PARTNER_SPEC_URL} variant="light" download="partner-openapi.json">
            {t("developers.download_spec")}
          </Button>
          <Button component={Link} to="/business" variant="subtle">
            {t("developers.portal")}
          </Button>
        </Group>
      </Stack>

      <Stack gap="sm" component="section" aria-labelledby="dev-steps">
        <Title order={2} size="h3" id="dev-steps">
          {t("developers.steps_title")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {STEPS.map((s, i) => (
            <Paper key={s} withBorder p="md" radius="md" data-testid={`step-${s}`}>
              <Stack gap="xs">
                <ThemeIcon radius="xl" size="md" aria-hidden>
                  {i + 1}
                </ThemeIcon>
                <Title order={3} size="h5">
                  {t(`developers.step_${s}_title`)}
                </Title>
                <Text size="sm">{t(`developers.step_${s}_body`)}</Text>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Example title={t("developers.example_token")} hint={t("developers.example_token_hint")} code={token} />
        <Example title={t("developers.example_resolve")} hint={t("developers.example_resolve_hint")} code={resolve} />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper withBorder p="md" radius="md" component="section" aria-labelledby="dev-rules">
          <Stack gap="xs">
            <Title order={2} size="h4" id="dev-rules">
              {t("developers.rules_title")}
            </Title>
            <Box component="ul" m={0} pl="md">
              {(["approval", "kenya", "purposes", "not_found", "limits", "billing", "webhooks"] as const).map((r) => (
                <Text component="li" size="sm" key={r} mb={6}>
                  {t(`developers.rule_${r}`)}
                </Text>
              ))}
            </Box>
          </Stack>
        </Paper>
        <Paper withBorder p="md" radius="md" component="section" aria-labelledby="dev-sandbox">
          <Stack gap="xs">
            <Title order={2} size="h4" id="dev-sandbox">
              {t("developers.sandbox_title")}
            </Title>
            <Text size="sm">{t("developers.sandbox_body")}</Text>
            {/* Narrow enough to wrap at 360 px: no scroll region to reach by keyboard. */}
            <Table verticalSpacing={4} fz="sm" aria-labelledby="dev-sandbox">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("developers.sandbox_code")}</Table.Th>
                  <Table.Th>{t("developers.sandbox_consent")}</Table.Th>
                  <Table.Th>{t("developers.sandbox_one_time")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {SANDBOX.map(([code, consent, oneTime]) => (
                  <Table.Tr key={code}>
                    <Table.Td>
                      <Code>{code}</Code>
                    </Table.Td>
                    <Table.Td>{consent === "none" ? t("developers.sandbox_none") : consent}</Table.Td>
                    <Table.Td>{t(`developers.sandbox_${oneTime}`)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        </Paper>
      </SimpleGrid>

      <Stack gap="sm" component="section" aria-labelledby="reference" id="reference-section">
        <Title order={2} size="h3" id="reference">
          {t("developers.reference_title")}
        </Title>
        <Text size="sm" c="dimmed">
          {t("developers.reference_hint")}
        </Text>
        {/* Swagger UI has its own light styles; it keeps a light surface in dark mode. */}
        <Paper
          withBorder
          radius="md"
          p={{ base: 0, sm: "xs" }}
          bg="white"
          c="dark.9"
          style={{ colorScheme: "light", overflowX: "auto" }}
        >
          <Suspense fallback={<Loader size="sm" m="md" aria-label={t("common.loading")} />}>
            <SwaggerView />
          </Suspense>
        </Paper>
        <Text size="xs" c="dimmed">
          {t("developers.reference_static")}{" "}
          <Anchor href="/api/v1/partner/docs" size="xs" underline="always">
            {t("developers.reference_static_link")}
          </Anchor>
        </Text>
      </Stack>
    </Stack>
  );
}

function Example({ title, hint, code }: { title: string; hint: string; code: string }) {
  const { t } = useTranslation();
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {title}
          </Title>
          <CopyButton value={code} timeout={3000}>
            {({ copied, copy }) => (
              <Button size="xs" variant="light" onClick={copy} aria-label={t("secret.copy_label", { label: title })}>
                {copied ? t("common.copied") : t("common.copy")}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Text size="sm" c="dimmed">
          {hint}
        </Text>
        <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          {code}
        </Code>
      </Stack>
    </Paper>
  );
}
